// @flow

import queryString from 'query-string';
import React, { Component } from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import { Carousel } from '@riseart/antd-provider';
import { LazyloadPlaceholder } from '@riseart/common';
import { LocalizedLink } from 'shared_components/i18n/LocalizedLink';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import { Picture } from 'shared_components/common/artdirection/picture/Picture';
import {
  artCarouselCls,
  artCarouselItemCls,
} from 'shared_components/artist/sections/art/details/Details.less';

type Props = {
  history: Object,
  location: Object,
  items: Array<Object>,
  pagination: Object,
  itemIdx: number,
};

type State = { initialSlide: number };

const LAZYLOAD_DEFAULT = {
  once: true,
  height: 300,
  offset: 200,
  placeholder: <LazyloadPlaceholder height={300} />,
};

/**
 * PrevArrow
 *
 * @param {Object} props
 */
function PrevArrow({ currentSlide, className, slider, handleClick, hide }: Object) {
  return hide ? null : (
    <button
      type="button"
      className={className && className.replace(' slick-disabled', '')}
      onClick={handleClick(currentSlide, slider)}
    >
      <FormattedMessage id="common.previous">{(text: string) => text}</FormattedMessage>
    </button>
  );
}

/**
 * NextArrow
 *
 * @param {Object} props
 */
function NextArrow({ currentSlide, className, slider, handleClick, hide }: Object) {
  return hide ? null : (
    <button
      type="button"
      className={className && className.replace(' slick-disabled', '')}
      onClick={handleClick(currentSlide, slider)}
    >
      <FormattedMessage id="common.next">{(text: string) => text}</FormattedMessage>
    </button>
  );
}

/**
 * ArtistArtCarousel
 *
 * @param {Props} props
 */
export class ArtistArtCarousel extends Component<Props, State> {
  triggeredByAction: boolean = false;

  triggeredByHash: boolean = false;

  slider: Object;

  currentSlide: number;

  /**
   * constructor
   *
   * @param {Props} props
   */
  constructor(props: Props) {
    super(props);

    this.slider = React.createRef();
    this.bindMethods();
    const { itemIdx } = this.props;
    this.currentSlide = itemIdx;
    this.state = { initialSlide: itemIdx > -1 ? itemIdx : 0 };
  }

  /**
   * componentDidUpdate
   *
   * @param {Props} prevProps
   */
  componentDidUpdate(prevProps: Props) {
    if (!this.triggeredByAction && prevProps.itemIdx !== this.props.itemIdx) {
      this.slider.current.goTo(this.props.itemIdx);
      this.triggeredByHash = true;
    } else {
      this.triggeredByAction = false;
    }
  }

  /**
   * bindMethods
   */
  bindMethods() {
    this.handleBeforeChange = this.handleBeforeChange.bind(this);
    this.buildUrl = this.buildUrl.bind(this);
  }

  /**
   * handleBeforeChange
   *
   * @param {number} from
   * @param {number} to
   */
  handleBeforeChange: Function;

  handleBeforeChange(from: number, to: number) {
    this.currentSlide = to;

    if (this.triggeredByHash) {
      this.triggeredByHash = false;
      return;
    }

    this.triggeredByAction = true;
    const { location, items } = this.props;
    const { id } = items[to] || {};

    if (id) {
      this.props.history.push(
        `${location.pathname}${location.search}#${encodeURIComponent(`detail=${id}`)}`,
      );
    }
  }

  /**
   * handlePrevious
   *
   * @param {number} currentSlide
   * @param {Object} slider
   * @returns {Function}
   */
  handlePrevious: Function =
    (currentSlide: number, slider: Object): Function =>
    () => {
      this.triggeredByAction = true;
      const loadPrevPage = this.buildUrl('previous')(currentSlide);

      if (!loadPrevPage) {
        slider.current.goTo(currentSlide - 1);
      }
    };

  /**
   * handleNext
   *
   * @param {number} currentSlide
   * @param {Object} slider
   * @returns {Function}
   */
  handleNext: Function =
    (currentSlide: number, slider: Object): Function =>
    () => {
      this.triggeredByAction = true;
      const loadNextPage = this.buildUrl('next')(currentSlide);

      if (!loadNextPage) {
        slider.current.goTo(currentSlide + 1);
      }
    };

  /**
   * buildUrl
   *
   * @param {string} button
   * @returns {boolean} Show if next page should be loaded by carousel:
   *          false: load by carousel,
   *          true: slide will be loaded by a controlled logic outside of carousel
   *          (e.g. initial slide set by url hash value)
   */
  buildUrl: Function =
    (button: string): Function =>
    (currentSlide: number): boolean => {
      const { pagination, items, location } = this.props;
      const totalItemsOnPage = items.length;

      if (button === 'previous' && currentSlide === 0 && pagination.currentPage > 0) {
        const newPage = pagination.currentPage - 1;
        const { page, ...parsedQueryString } = queryString.parse(location.search);
        const newQueryString = queryString.stringify({
          ...parsedQueryString,
          ...(newPage > 1 ? { page: newPage } : {}),
        });

        this.props.history.push(
          `${location.pathname}${newQueryString ? `?${newQueryString}` : ''}#${encodeURIComponent(
            `detail=last`,
          )}`,
        );

        return true;
      }

      if (
        button === 'next' &&
        currentSlide === totalItemsOnPage - 1 &&
        pagination.currentPage < pagination.totalPages
      ) {
        const newPage = pagination.currentPage + 1;
        const parsedQueryString = queryString.parse(location.search);
        const newQueryString = queryString.stringify({
          ...parsedQueryString,
          page: newPage,
        });

        this.props.history.push(
          `${location.pathname}${newQueryString ? `?${newQueryString}` : ''}#${encodeURIComponent(
            `detail=first`,
          )}`,
        );

        return true;
      }

      return false;
    };

  /**
   * render
   */
  render(): Node {
    const { items, pagination, itemIdx } = this.props;
    const totalItems = items && items.length;

    return (
      <Carousel
        ref={this.slider}
        dots={false}
        arrows
        infinite={false}
        itemsPerSlide={1}
        className={artCarouselCls}
        initialSlide={this.state.initialSlide}
        beforeChange={this.handleBeforeChange}
        prevArrow={
          <PrevArrow
            slider={this.slider}
            handleClick={this.handlePrevious}
            hide={pagination.currentPage === 1 && itemIdx === 0}
          />
        }
        nextArrow={
          <NextArrow
            slider={this.slider}
            handleClick={this.handleNext}
            hide={pagination.totalPages === pagination.currentPage && itemIdx === items.length - 1}
          />
        }
      >
        {items &&
          items.map((item, key) => (
            <div key={`${item.slug}${key}`} className={artCarouselItemCls}>
              <LocalizedLink
                assembler={{ handler: UrlAssembler.artDetail, args: [item.id, item.slug] }}
                title={item.title}
              >
                <Picture
                  image={
                    extractImageFileFromData.artImage(item.images, 'TYPE_FLAT_MASTER', true) ||
                    extractImageFileFromData.artImage(item.images, 'TYPE_MAIN_MASTER')
                  }
                  alt={item.title}
                  artDirectionKey="art.main"
                  type="gallery.fullWidth"
                  lazyload={LAZYLOAD_DEFAULT}
                />
              </LocalizedLink>
            </div>
          ))}
        {/*
          An empty element is added for an edge case when only one item is shown in slider,
          which makes the carousel to hide the arrows. In that case we add an empty element,
          so that the previous arrow is shown. This is a way to show the arrows if they are not required,
          but in this case they are required to go to the previous page slider.
        */}
        {pagination.totalPages > 1 && totalItems === 1 ? <div /> : null}
      </Carousel>
    );
  }
}
